import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useState } from "react";
import PrescribedMatchReview from "./PrescribedMatchReview";
import { displayName } from "./nameMask";
import { usageConfidence } from "./estate_model";
import * as XLSX from "xlsx";
const API = apiUrl("");
const number = n => n == null ? "—" : n.toLocaleString(undefined, {
  maximumFractionDigits: 3
});
const cell = {
  padding: "10px",
  borderBottom: "1px solid #F0F0F0",
  textAlign: "left",
  verticalAlign: "top"
};
// Units per pack. dm+d packs measured in doses (inhalers, "120 dose") count
// as one device per pack, because reports count inhalers - unless the
// report's quantities are whole multiples of the dose count. Mirrors
// packDivisor() in usage_prescribed_dispensed.js.
function dosesOf(m) {
  if (!(Number(m?.qtval) > 0)) return null;
  for (const n of [m.vmppNm, m.amppNm]) {
    const h = /\b(\d+)\s*doses?\b/i.exec(String(n || ""));
    if (h && Number(h[1]) === Number(m.qtval)) return Number(m.qtval);
  }
  return null;
}
function divisorFor(m, row) {
  const q = Number(m?.qtval) > 0 ? Number(m.qtval) : null,
    d = dosesOf(m);
  if (!d) return q;
  const qs = (row?.months || []).map(x => x.units).filter(x => x > 0);
  return qs.length && qs.every(x => x % d === 0) ? d : 1;
}
function Bars({
  months,
  measure = "units"
}) {
  const peak = Math.max(1, ...months.map(m => Math.abs(m[measure] || 0)));
  return <div style={{
    display: "flex",
    gap: 12,
    alignItems: "end"
  }}>
    {months.map(m => <div key={m.month} title={m.why || (!m.complete ? "Partial or missing branch coverage" : "Full month")} style={{
      textAlign: "center",
      minWidth: 55
    }}>
      <div style={{
        height: 42,
        display: "flex",
        alignItems: "end",
        justifyContent: "center"
      }}>
        <div style={{
          width: 20,
          height: m[measure] ? Math.max(3, Math.abs(m[measure]) / peak * 42) : 0,
          background: m.complete ? "#287de3" : "#d89a2c",
          borderBottom: !m[measure] ? "2px solid #bbb" : undefined
        }} />
      </div>
      <strong style={{
        fontSize: 12
      }}>{number(m[measure])}</strong>
      <div style={{
        fontSize: 11,
        color: "#5D5D5D"
      }}>{m.month.slice(4)}/{m.month.slice(0, 4)}</div>
    </div>)}
  </div>;
}
function Reliability({
  row
}) {
  const result = usageConfidence(row.months.map(m => ({
    qty: m.units
  })));
  const changingCoverage = row.branches && row.months.some(m => !m.complete);
  const level = row.partial || changingCoverage ? "low" : result.level;
  return <span title={row.partial || changingCoverage ? "Partial months or differing branch coverage; average uses available branch months" : result.why} style={{
    color: level === "high" ? "#15803d" : level === "medium" ? "#a16207" : "#b45309"
  }}>
    {level === "high" ? "High" : level === "medium" ? "Medium" : level === "none" ? "No usage" : "Low"}
    {(row.partial || changingCoverage) && " · incomplete coverage"}
  </span>;
}
function Matching({
  row,
  onAccept,
  costs = false
}) {
  const m = row.match,
    accepted = m?.status === "matched" ? m.match : null;
  return <div style={{
    fontSize: 12
  }}>
    {accepted?.productName && <div style={{
      fontWeight: 600
    }}>{accepted.productName}</div>}
    {accepted?.amppNm && <div>Trade: {accepted.amppNm}</div>}
    {accepted?.vmppNm && <div>Generic: {accepted.vmppNm}</div>}
    {accepted?.supplier && <div>Supplier: {accepted.supplier}{accepted.method === "supplier" && <span style={{
        color: "#15803d"
      }}> · matched using supplier</span>}
      {accepted.supplierFrom === "dispensed" && <span style={{
        color: "#15803d"
      }}> · supplier taken from what was dispensed</span>}</div>}

    <div>{accepted?.qtval || "—"} <span style={{
        color: "#5D5D5D"
      }}>pack size</span>
      {accepted?.packSource === "dispensed" && <span style={{
        color: "#15803d"
      }}> · confirmed from compatible dispensed packs</span>}
      {accepted && dosesOf(accepted) && divisorFor(accepted, row) === 1 && <span style={{
        color: "#5D5D5D"
      }}> (doses; report counts inhalers, so 1 inhaler = 1 pack)</span>}</div>
    <div>{accepted?.priceGbp != null ? `£${Number(accepted.priceGbp).toFixed(2)}` : "—"} <span style={{
        color: "#5D5D5D"
      }}>reference pack price</span></div>
    {costs && <div><strong>Total reference cost: {row.referenceTotalCost != null ? `£${Number(row.referenceTotalCost).toFixed(2)}` : "—"}</strong></div>}
    <div>{accepted ? "Product matched" : m?.status || "pending"}</div>
    {accepted?.packStatus === "multiple-packs" && <div>Several dm+d packs available</div>}
    {accepted?.packStatus === "price-options" && <div>Several dm+d pack prices available</div>}
    {!!m?.candidates?.length && <details><summary>{accepted ? "dm+d packs / prices" : "Match candidates"}</summary>
      {m.candidates.map((c, i) => <div key={i} style={{
        paddingTop: 4
      }}>{c.amppNm} · pack {c.qtval} · score {c.score}
        {c.supplier && <span style={{
          color: c.supplierMatch === true ? "#15803d" : c.supplierMatch === false ? "#5D5D5D" : undefined
        }}>{` · ${c.supplier}`}{c.supplierMatch === true && " ✓ supplier"}</span>}
        {c.priceGbp != null && ` · £${Number(c.priceGbp).toFixed(2)}`}
        {costs && row.total != null && divisorFor(c, row) > 0 && c.priceGbp != null && ` · reference total £${(row.total / divisorFor(c, row) * c.priceGbp).toFixed(2)}`}
        {(!accepted || accepted.packStatus === "multiple-packs" || accepted.packStatus === "price-options") && c.amppid && <button style={{
          marginLeft: 6
        }} onClick={() => onAccept(row, c)}>{accepted ? "Use this pack" : "Use this match"}</button>}</div>)}
      <div style={{
        color: "#5D5D5D",
        marginTop: 4
      }}>{accepted ? "Product accepted. Pack size can also be confirmed from compatible dispensed records; a price is applied only when unambiguous or selected." : "Candidates await review; their prices and pack sizes are not applied."}</div>
    </details>}
  </div>;
}
// "Actually dispensed": what the pharmacy handed out against each prescribed
// item. Validated pack information can corroborate the prescribed reference pack.
function ActuallyDispensed({
  row
}) {
  if (!Array.isArray(row.children)) return <strong style={{
    color: "#CF1322"
  }}>Updated backend required</strong>;
  if (!row.children.length) return <span style={{
    fontSize: 12
  }}>Nothing dispensed against this in the report window</span>;
  const small = {
    ...cell,
    padding: "4px 6px",
    fontSize: 12
  };
  return <table style={{
    borderCollapse: "collapse",
    width: "100%",
    minWidth: 380
  }}><thead><tr>
    <th style={small}>Dispensed product / manufacturer</th>
    {row.months.map(m => <th key={m.month} style={small}>{m.month.slice(4)}/{m.month.slice(0, 4)}</th>)}
    <th style={small}>Total</th>
  </tr></thead><tbody>{row.children.map(c => <tr key={c.itemKey}>
    <td style={small}><strong>{c.core || c.name}{c.core && c.supplier ? ` (${c.supplier})` : ""}</strong>
      <div>Pack: {c.packSize ? `${c.packSize}${c.packUnit || ""}` : "Not stated"}</div></td>
    {c.months.map(m => <td key={m.month} style={small}><strong>{number(m.units)}</strong> {c.quantityLabel || "units"}
      {m.packets != null && <div>{number(m.packets)} packs</div>}</td>)}
    <td style={small}><strong>{number(c.total)}</strong> {c.quantityLabel || "units"}
      {c.totalPackets != null && <div><strong>{number(c.totalPackets)} packs</strong></div>}</td>
  </tr>)}</tbody></table>;
}
function SourceQuantity({
  row
}) {
  return <><strong>{row.name}</strong><div style={{
      marginTop: 8,
      display: "flex",
      gap: 14
    }}>
    {row.months.map(m => <div key={m.month}><div style={{
          fontSize: 11,
          color: "#5D5D5D"
        }}>{m.month.slice(4)}/{m.month.slice(0, 4)}</div>
      <strong>{number(m.units)}</strong></div>)}
    </div><div>{row.quantityLabel || "units"}</div>
    <div><strong>Total: {number(row.total)} {row.quantityLabel || "units"}</strong></div>
    <div>Avg/month: {number(row.average)} {row.quantityLabel || "units"}</div>
    <Reliability row={row} /></>;
}
// Prescribed packs per month = prescribed quantity ÷ pack size of the
// prescribed item's accepted dm+d match. Until a match (with a single pack
// size) is accepted, the bars show the prescribed quantity instead.
const avgBox = {
  marginTop: 6,
  padding: "4px 12px",
  background: "#E6F7FF",
  border: "1px solid #91D5FF",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 700,
  color: "#0c4a6e"
};
function PrescribedPacks({
  row
}) {
  const accepted = row.match?.status === "matched" ? row.match.match : null;
  const pack = accepted ? row.packDivisor ?? divisorFor(accepted, row) : null;
  const inhalers = accepted && dosesOf(accepted) && pack === 1;
  if (!pack) return <div style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center"
  }}>
    <Bars months={row.months} />
    <div>Prescribed {row.quantityLabel || "units"} / month</div><strong>Total: {number(row.total)} {row.quantityLabel || "units"}</strong>
    <div style={avgBox}>Avg {row.quantityLabel || "units"} per month: {number(row.average)}</div>
    <div style={{
      fontSize: 11,
      color: "#5D5D5D"
    }}>Accept a dm+d match to show packs</div></div>;
  const months = row.months.map(m => ({
    ...m,
    packs: m.units == null ? null : Math.round(m.units / pack * 100) / 100
  }));
  return <div style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center"
  }}>
    <Bars months={months} measure="packs" />
    <div>{inhalers ? `Prescribed inhalers (${dosesOf(accepted)} doses each) / month` : `Prescribed packs of ${pack} / month`}</div>
    <strong>Total: {row.total == null ? "—" : number(Math.round(row.total / pack * 100) / 100)} packs</strong>
    <div style={avgBox}>Avg packs per month: {row.average == null ? "—" : number(Math.round(row.average / pack * 100) / 100)}</div></div>;
}
export default function UsagePairGrid({
  open,
  onClose,
  groupId,
  members = [],
  defaultPharmacyId = "",
  masked = false
}) {
  const [manualReview, setManualReview] = useState(false);
  const [pharmacyId, setPharmacy] = useState(defaultPharmacyId);
  const side = "prescribed"; // this view follows what was PRESCRIBED
  const relationship = "both"; // Always show every prescribed/dispensed pairing.
  const [search, setSearch] = useState(""),
    [aggregate, setAggregate] = useState(false);
  const [page, setPage] = useState(0),
    [data, setData] = useState(null),
    [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [matchingImpact, setMatchingImpact] = useState(null);
  const [busy, setBusy] = useState(false),
    [expanded, setExpanded] = useState({}),
    [refresh, setRefresh] = useState(0);
  useEffect(() => {
    setPharmacy(defaultPharmacyId || "");
    setMatchingImpact(null);
  }, [defaultPharmacyId, groupId]);
  useEffect(() => {
    if (!open || !groupId) return undefined;
    const controller = new AbortController();
    setBusy(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/usagePairGrid`, {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`
          },
          body: JSON.stringify({
            groupId,
            pharmacyId,
            side,
            relationship,
            search,
            aggregate,
            page
          })
        });
        const result = await r.json();
        if (!r.ok) throw new Error(result.error || "Could not load usage");
        setData(result);
      } catch (e) {
        if (e.name !== "AbortError") {
          setError(e.message);
          setData(null);
        }
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, groupId, pharmacyId, side, relationship, search, aggregate, page, refresh]);
  if (!open) return null;
  const change = setter => e => {
    setter(e.target.value);
    setPage(0);
    setExpanded({});
  };
  const accept = async (row, candidate, targetSide = side) => {
    try {
      const response = await fetch(`${API}/usagePairAcceptMatch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`
        },
        body: JSON.stringify({
          groupId,
          side: targetSide,
          key: row.itemKey,
          amppid: candidate.amppid
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not accept match");
      setRefresh(n => n + 1);
    } catch (e) {
      setError(e.message);
    }
  };
  // Export every row matching the current filters (all pages, not just the
  // one on screen) to Excel: one sheet for prescribed items, one for what was
  // actually dispensed against them.
  const exportExcel = async () => {
    setExporting(true);
    setError("");
    try {
      const all = [];
      let months = [];
      for (let p = 0;; p++) {
        const r = await fetch(`${API}/usagePairGrid`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`
          },
          body: JSON.stringify({
            groupId,
            pharmacyId,
            side,
            relationship,
            search,
            aggregate,
            page: p
          })
        });
        const result = await r.json();
        if (!r.ok) throw new Error(result.error || "Could not load usage for export");
        months = result.months || months;
        all.push(...(result.rows || []));
        if (!result.rows?.length || (p + 1) * result.pageSize >= result.total) break;
      }
      const label = m => `${m.slice(4)}/${m.slice(0, 4)}`;
      const prescribed = [],
        dispensed = [];
      const addRow = (row, level) => {
        const accepted = row.match?.status === "matched" ? row.match.match : null;
        const pack = accepted ? row.packDivisor ?? divisorFor(accepted, row) : null;
        const out = {
          Level: level,
          Pharmacy: row.pharmacyName,
          "Prescribed item": row.name,
          Unit: row.quantityLabel || "units"
        };
        row.months.forEach((m, i) => {
          out[`Qty ${label(months[i] || m.month)}`] = m.units;
        });
        out["Total qty"] = row.total;
        out["Avg/month"] = row.average;
        row.months.forEach((m, i) => {
          out[`Packs ${label(months[i] || m.month)}`] = pack && m.units != null ? Math.round(m.units / pack * 100) / 100 : null;
        });
        out["Total packs"] = pack && row.total != null ? Math.round(row.total / pack * 100) / 100 : null;
        out["Avg packs/month"] = pack && row.average != null ? Math.round(row.average / pack * 100) / 100 : null;
        out["Match status"] = accepted ? accepted.method === "supplier" ? "matched (supplier)" : "matched" : row.match?.status || "pending";
        out["dm+d product"] = accepted?.productName || "";
        out["dm+d trade pack"] = accepted?.amppNm || "";
        out["dm+d generic pack"] = accepted?.vmppNm || "";
        out["dm+d supplier"] = accepted?.supplier || "";
        out["dm+d pack size"] = pack;
        out["dm+d pack price £"] = accepted?.priceGbp ?? null;
        out["Reference total cost £"] = row.referenceTotalCost ?? null;
        out["Top candidate (unmatched)"] = !accepted && row.match?.candidates?.[0] ? `${row.match.candidates[0].amppNm} (score ${row.match.candidates[0].score})` : "";
        prescribed.push(out);
        for (const c of row.children || []) {
          const d = {
            Level: level,
            Pharmacy: row.pharmacyName,
            "Prescribed item": row.name,
            "Dispensed product": c.core || c.name,
            Manufacturer: c.supplier || "",
            "Report pack size": c.packSize || null,
            Unit: c.quantityLabel || "units"
          };
          c.months.forEach((m, i) => {
            d[`Qty ${label(months[i] || m.month)}`] = m.units;
          });
          d["Total qty"] = c.total;
          c.months.forEach((m, i) => {
            d[`Packs ${label(months[i] || m.month)}`] = m.packets;
          });
          d["Total packs"] = c.totalPackets;
          dispensed.push(d);
        }
      };
      for (const row of all) {
        addRow(row, aggregate ? "All branches" : "Branch");
        if (aggregate) for (const b of row.branches || []) addRow(b, "Branch");
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prescribed), "Prescribed");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dispensed), "Actually dispensed");
      const showLabel = {
        both: "All items",
        same: "Dispensed as prescribed",
        different: "Dispensed as something else"
      }[relationship];
      const branch = pharmacyId ? members.find(m => m.pharmacyId === pharmacyId)?.pharmacyName || pharmacyId : "All branches";
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([["Usage analysis: prescribed"], ["Exported", new Date().toLocaleString()], ["Pharmacy", branch], ["Show", showLabel], ["Search", search || "(none)"], ["Aggregate branches", aggregate ? "on" : "off"], ["Prescribed items", all.length], ["Months", months.map(label).join(", ")], ["Note", "Packs = prescribed quantity ÷ dm+d pack size of the accepted match. Reference cost is not the report's recorded cost."]]), "Filters");
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `usage_prescribed_${stamp}.xlsx`);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };
  const runMatching = async (dryRun = false) => {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API}/usagePairResolveMatches`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`
        },
        body: JSON.stringify({
          groupId,
          dryRun
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not run matching");
      setMatchingImpact(result);
      if (!dryRun) setRefresh(n => n + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return <div role="dialog" aria-modal="true" aria-label="Prescribed usage" style={{
    position: "fixed",
    inset: 0,
    background: "#0006",
    zIndex: 10000,
    padding: "3vh 3vw"
  }}>
    <div style={{
      background: "white",
      borderRadius: 10,
      maxHeight: "94vh",
      overflow: "auto",
      padding: 22
    }}>
      {manualReview && <PrescribedMatchReview groupId={groupId} onClose={() => setManualReview(false)} onChanged={() => {
        setRefresh(n => n + 1);
        setMatchingImpact(null);
      }} />}
      <div style={{
        display: "flex",
        justifyContent: "space-between"
      }}><h2 style={{
          marginTop: 0
        }}>Usage analysis: prescribed</h2><button onClick={onClose}>Close</button></div>
      <div style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "center"
      }}>
        <label>Pharmacy <select value={pharmacyId} onChange={change(setPharmacy)}><option value="">All branches</option>
          {members.map(m => <option key={m.pharmacyId} value={m.pharmacyId}>{displayName(m.pharmacyName || m.pharmacyId, masked)}</option>)}
        </select></label>
        <span>All items</span>
        <button aria-pressed={aggregate} onClick={() => {
          setAggregate(!aggregate);
          setPage(0);
          setExpanded({});
        }}>Aggregate branches: {aggregate ? "on" : "off"}</button>
        <input aria-label="Search all report text" placeholder="Search any report text or pharmacy…" value={search} onChange={change(setSearch)} style={{
          minWidth: 260
        }} />
        <button onClick={() => setManualReview(true)}>Match unmatched prescribed items</button>
        <button disabled={busy} onClick={() => runMatching(true)}>Preview matching changes</button>
        <button disabled={busy || !!(matchingImpact && matchingImpact.algorithmVersion !== 10)} onClick={() => runMatching(false)}>Run matching</button>
        <button disabled={busy || exporting || !data?.total} onClick={exportExcel}>{exporting ? "Exporting…" : `Export to Excel${data?.total ? ` (${data.total})` : ""}`}</button>
      </div>
      <p style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>This view follows what was <strong>prescribed</strong>, because that is the demand the pharmacy has to cope with. Each row is a prescribed item with its monthly quantity over the latest three completed report months. Packs per month and the reference cost use the prescribed item’s dm+d match: quantity ÷ dm+d pack size (× dm+d price for cost). The reference cost is not the report’s recorded cost.</p>
      <p style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}><strong>Actually dispensed</strong> (far right) lists what was handed out against each prescribed item, with its own quantities. Compatible dm+d-matched dispensed packs can supply a missing reference pack size; the prescription still controls which drug and brand are required. </p>
      {matchingImpact && <section aria-label="Matching impact" style={{
        padding: 12,
        background: "#F0F7FF",
        border: "1px solid #BAD7FF",
        borderRadius: 8,
        margin: "10px 0"
      }}>
        <strong>{matchingImpact.method === "staged" ? "Staged dispensing preview — pack → supplier → name — no matches saved" : matchingImpact.scope === "dispensed" ? "Dispensed-only preview — no matches saved" : matchingImpact.dryRun ? "Preview — no matches saved" : "Matching completed"}</strong>
        <p>{matchingImpact.examined || 0} automatic {matchingImpact.scope === "dispensed" ? "dispensed products" : "matches"} checked across the group (all branches). {matchingImpact.manualPreserved || 0} manual decisions preserved. {matchingImpact.changed || 0} changes{matchingImpact.dryRun ? " proposed" : " applied"}.</p>
        <p>Matcher version: <strong>{matchingImpact.algorithmVersion ?? "not reported"}</strong>{matchingImpact.catalogueRows != null && <> · {matchingImpact.catalogueRows.toLocaleString()} dm+d rows loaded</>}</p>
        {matchingImpact.algorithmVersion !== 10 && <p role="alert" style={{
          color: "#A8071A",
          fontWeight: 600
        }}>This result came from an older backend matcher. Deploy the updated backend files and restart the backend, then preview again.</p>}
        {matchingImpact.sideBreakdown && <table style={{
          borderCollapse: "collapse",
          fontSize: 13
        }}><thead><tr><th>Matched records</th><th style={{
                padding: "4px 14px"
              }}>Before</th><th>After</th></tr></thead><tbody>{["prescribed", "dispensed"].map(side => <tr key={side}><td>{side === "prescribed" ? "Prescribed products" : "Dispensed products"}</td><td style={{
                padding: "4px 14px"
              }}>{matchingImpact.sideBreakdown[side]?.before?.matched || 0}</td><td>{matchingImpact.sideBreakdown[side]?.after?.matched || 0}</td></tr>)}</tbody></table>}
        {matchingImpact.concurrentChangesSkipped > 0 && <p>{matchingImpact.concurrentChangesSkipped} item(s) were skipped because a manual decision changed during the run. Refresh and preview again; the counts below describe the attempted automatic results.</p>}
        {matchingImpact.before && <table style={{
          borderCollapse: "collapse",
          fontSize: 13
        }}><thead><tr><th>Status</th><th style={{
                padding: "4px 14px"
              }}>Before</th><th>After</th></tr></thead>
          <tbody>{["matched", "review", "unmatched", "pending"].map(status => <tr key={status}><td>{status}</td><td style={{
                padding: "4px 14px"
              }}>{matchingImpact.before[status] || 0}</td><td>{matchingImpact.after?.[status] || 0}</td></tr>)}</tbody></table>}
        {matchingImpact.diagnostics && <>
          <p><strong>{matchingImpact.after?.matched || 0} of {matchingImpact.examined} dispensed products matched ({matchingImpact.matchRate || 0}%).</strong> These are distinct automatic product records, not prescription quantities; manual decisions are excluded.</p>
          <p>Matched with GTIN(s): {matchingImpact.diagnostics.withGtin}. Matched without GTIN: {matchingImpact.diagnostics.withoutGtin}. Single dm+d pack: {matchingImpact.diagnostics.singlePack}. Multiple compatible packs: {matchingImpact.diagnostics.multiplePacks}. Multiple packs or GTINs do not identify one unique barcode.</p>
          {matchingImpact.diagnostics.stages && <><p>Explicit device-to-dose conversions: {matchingImpact.diagnostics.doseConversions || 0}.</p>
            <ul>{Object.entries(matchingImpact.diagnostics.stages).map(([stage, count]) => <li key={stage}>{stage}: {count}</li>)}</ul></>}
          {!!matchingImpact.diagnostics.reviewItems.length && <details><summary>Unresolved dispensed products (first 200)</summary>
            <ul>{matchingImpact.diagnostics.reviewItems.map((item, i) => <li key={i}>
              <strong>{item.name}</strong> · Manufacturer: {item.supplier || "missing"} · Pack: {item.packSize || "missing"} — {item.reason}{item.packCandidates != null ? ` · ${item.packCandidates} pack candidates → ${item.supplierCandidates} supplier candidates` : ""}
              <ul>{item.candidates.map((c, j) => <li key={j}>{c.name} · {c.supplier || "manufacturer missing"} · Pack {c.packSize || "unknown"} · score {c.score}</li>)}</ul>
            </li>)}</ul>
          </details>}
        </>}
        {!!matchingImpact.changes?.length && <details><summary>Changed items ({matchingImpact.changed}; showing up to 200)</summary>
          <ul>{matchingImpact.changes.map((change, i) => <li key={i}>{change.side}: {change.name} — {change.before} → {change.after}{change.product ? ` · ${change.product}` : ""}</li>)}</ul>
        </details>}
      </section>}
      <p style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>All items are shown. Each report line pairs a prescribed item with what was dispensed for it.</p>
      <p style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>Missing uploads show —; an uploaded month with no use shows 0. Orange bars indicate partial coverage. Branch averages use available months; an aggregate average sums its component branch averages. Green cells have accepted dm+d matches.</p>
      {busy && <p role="status">Loading…</p>}{error && <p role="alert" style={{
        color: "#CF1322"
      }}>{error}</p>}
      {!busy && data && !data.available && <p>Upload a report containing prescribed and dispensed columns for a selected pharmacy to start.</p>}
      {!busy && data?.available && !data.rows.length && <p>No usage matches these filters.</p>}
      {!busy && !!data?.rows?.length && <>
        {data.schemaVersion !== 4 && <p role="alert" style={{
          color: "#CF1322",
          fontWeight: 600
        }}>The backend response is missing the updated linked-grid fields. Replace usage_prescribed_dispensed.js on the API server and restart the backend. No re-upload is needed.</p>}
        <div style={{
          overflowX: "auto"
        }}><table style={{
            width: "100%",
            borderCollapse: "collapse"
          }}><thead><tr>
          {["Pharmacy", "Prescribed item and monthly quantity", "Prescribed packs per month", "Prescribed dm+d match / reference cost", "Actually dispensed"].map(h => <th key={h} style={cell}>{h}</th>)}
        </tr></thead><tbody>{data.rows.map(row => <React.Fragment key={row.id}>
          <tr><td style={cell}>{aggregate ? <button onClick={() => setExpanded(x => ({
                      ...x,
                      [row.id]: !x[row.id]
                    }))} aria-expanded={!!expanded[row.id]}>
            {expanded[row.id] ? "▾" : "▸"} {row.branches.length} branches</button> : displayName(row.pharmacyName, masked)}</td>
            <td style={{
                    ...cell,
                    minWidth: 245
                  }}><SourceQuantity row={row} /></td>
            <td style={{
                    ...cell,
                    minWidth: 215,
                    verticalAlign: "middle"
                  }}><PrescribedPacks row={row} /></td>
            <td style={{
                    ...cell,
                    minWidth: 260,
                    background: row.match?.status === "matched" ? "#F6FFED" : undefined
                  }}><Matching row={row} onAccept={accept} costs /></td>
            <td style={{
                    ...cell,
                    background: "#FAFAFA"
                  }}><ActuallyDispensed row={row} /></td></tr>
          {aggregate && expanded[row.id] && row.branches.map(branch => <tr key={branch.id} style={{
                  background: "#FAFAFA"
                }}>
            <td style={{
                    ...cell,
                    paddingLeft: 25
                  }}>{displayName(branch.pharmacyName, masked)}</td>
            <td style={cell}><SourceQuantity row={branch} /></td>
            <td style={{
                    ...cell,
                    verticalAlign: "middle"
                  }}><PrescribedPacks row={branch} /></td>
            <td style={cell}><Matching row={branch} onAccept={accept} costs /></td>
            <td style={cell}><ActuallyDispensed row={branch} /></td>
          </tr>)}
        </React.Fragment>)}</tbody></table></div>
        <div style={{
          display: "flex",
          gap: 12,
          marginTop: 16
        }}><button disabled={!page} onClick={() => setPage(page - 1)}>Previous</button>
          <span>{page * data.pageSize + 1}–{Math.min((page + 1) * data.pageSize, data.total)} of {data.total}</span>
          <button disabled={(page + 1) * data.pageSize >= data.total} onClick={() => setPage(page + 1)}>Next</button></div>
      </>}
    </div>
  </div>;
}
